// @ts-nocheck

import React, { useMemo, useRef, useCallback, useState, Fragment, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Trash, ChevronUp, ChevronDown } from '../../../assets/svg';
import BScrollView from '../../../components/BScrollView';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Formatter from '../../../i18n/formatter';
import { __, getCurrency } from '../../../i18n/l10n';
import { Button, SecondaryButton } from '../../../styled-components/shared';
import globalBasketUtils from '../../../utils/basket';
import { basketStatuses } from '../constants';
import { PANEL_PADDING } from '../constants/style';
import { getSubmitOptions, dispatchActionFromPayload } from '../utils';

import Keyboard from './Keyboard';
import StakeInput from './StakeInput';

const Wrap = styled(BScrollView)`
  border-radius: 2px;
  overflow-y: hidden;
  overflow-x: auto;
  background: rgba(0, 0, 0, 0.4);

  ${({ $totalHeight }) =>
    $totalHeight
      ? css`
          max-height: ${$totalHeight}px;
          height: auto;
        `
      : css`
          max-height: 100%;
          height: 100%;
        `}
`;

const TrashIcon = styled(Trash)`
  color: #fff;
  width: 1.4em;
  height: 1.4em;
`;

const StyledLoading = styled(LoadingSpinner)`
  width: 50%;
  height: 50%;
`;

const CenterWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3em 0;
`;

const CenterWrapperColumn = styled(CenterWrapper)`
  flex-direction: column;
  height: ${({ $totalHeight }) => ($totalHeight ? `${$totalHeight}px` : '100%')};
  overflow: hidden;
`;

const TextLine = styled.p`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5em 0;
  color: #fff;
  margin: 0;
  width: 100%;
`;

const LoadingText = styled.span`
  padding: 0.5em 0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  width: 100%;
`;

const RightLabel = styled.span`
  display: inline-block;
  max-width: 30%;
  text-align: right;
  padding: 0.2em;
  background-color: ${({ $highlight, theme }) => ($highlight ? theme.common.colors.secondary : 'transparent')};
  border-radius: 2px;
`;

const Label = styled.span`
  text-align: left;
`;

const LabelYellow = styled(Label)`
  color: ${({ theme }) => theme.common.colors.secondary};
`;

const ShortLabel = styled(Label)`
  width: 70%;
`;

const Cta = styled.div`
  position: absolute;
  bottom: ${PANEL_PADDING}px;
  left: 0;
  width: 100%;
  height: 3em;
  padding: 0 10px;
  display: flex;
  justify-content: center;
`;

const ClearButton = styled(SecondaryButton)`
  && {
    font-family: BettingBebasNeueRegular, sans-serif;
    border: 0;
    padding: 0 1em;
    background-color: rgba(0, 0, 0, 0.4);
    box-shadow: none;
    margin-right: 0.5em;
  }
`;

const MainButton = styled(SecondaryButton)`
  && {
    font-family: BettingBebasNeueRegular, sans-serif;
    margin: 0;
    padding: 0.5em;
    width: calc(100% - 3.4em);
    box-shadow: none;
  }
`;

const TertiaryButton = styled(Button)`
  && {
    font-family: BettingBebasNeueRegular, sans-serif;
    margin: 0;
    padding: 0.5em;
    width: 100%;
  }
`;

const StyledStakeInput = styled(StakeInput)`
  &&&& button {
    background-color: rgba(0, 0, 0, 0.4);
    color: ${({ theme }) => theme.common.colors.secondary};
  }
`;

const Sum = styled.div`
  padding: 0.5em;
  border-radius: 2px;
`;

const Message = styled.div`
  width: 100%;
  padding: 0 0.5em;
  color: #fff;
  text-align: left;
  border-radius: 2px;
  margin: 0;
  background: ${({ $status }) => {
    switch ($status) {
      case basketStatuses.VALIDATED:
        return 'rgba(42, 189, 42, 0.6)';
      case basketStatuses.WAITING_TRADER_VALIDATION:
        return 'rgba(255, 197, 20, 0.6)';
      default:
        return 'rgba(236, 65, 74, 0.6)';
    }
  }};
`;

const commonArrow = css`
  width: 1em;
  height: 1em;
  margin: 0 0.125em;
`;

const ArrowUp = styled(ChevronUp)`
  ${commonArrow}
  color: ${({ theme }) => theme.common.colors.positive};
`;

const ArrowDown = styled(ChevronDown)`
  ${commonArrow}
  color: ${({ theme }) => theme.common.colors.negative};
`;

const ImmersiveBasket = (props) => {
  const {
    betTitle,
    outcomeLabel,
    value,
    validatedValue,
    valueHasChanged,
    oddId,
    stake,
    message,
    status,
    clearBasketItem,
    submit,
    setStake,
    acceptChange,
    makeApiCall,
    onWaitingTraderValidation,
    onError,
    onValidated,
    availableHeight,
    openExternalLink,
  } = props;

  const [showKeyboard, setShowKeyboard] = useState(false);
  const [maxHeight, setMaxHeight] = useState(null);

  const fixedBottomComponentRef = useRef(null);
  const refScrollWrapper = useRef(null);

  const onValidateClick = useCallback(() => {
    if (stake > 0) {
      const hooks = {
        received: (response, body) => {
          const actions = { onWaitingTraderValidation, onError, onValidated, openExternalLink };
          dispatchActionFromPayload(body, actions);
        },
        failure: (err) => onError(err),
      };
      submit();
      makeApiCall({ options: getSubmitOptions(stake, value, oddId), hooks });
    }
  }, [makeApiCall, oddId, onError, onValidated, onWaitingTraderValidation, openExternalLink, stake, submit, value]);

  const onClearClick = useCallback(clearBasketItem, [clearBasketItem]);
  const onChangeStake = useCallback((value) => setStake(globalBasketUtils.formatStake(value)), [setStake]);
  const onClickPlusStake = useCallback(() => setStake(globalBasketUtils.incrementStake(stake)), [setStake, stake]);
  const onClickMinusStake = useCallback(
    () => setStake(globalBasketUtils.incrementStake(stake, true)),
    [setStake, stake],
  );
  const onClickStakeInput = useCallback(() => setShowKeyboard(true), []);
  const onCloseStakeInput = useCallback(() => setShowKeyboard(false), []);

  const isPending = useMemo(() => status === basketStatuses.PENDING, [status]);
  useEffect(() => {
    let newHeight = availableHeight - refScrollWrapper.current.getBoundingClientRect().top - PANEL_PADDING;
    if (!showKeyboard && !isPending)
      newHeight -= fixedBottomComponentRef.current.getBoundingClientRect().height + PANEL_PADDING;
    setMaxHeight(newHeight);
  }, [availableHeight, showKeyboard, isPending]);

  return (
    <Fragment>
      <Wrap $totalHeight={maxHeight} ref={refScrollWrapper}>
        {!showKeyboard && message !== null && message !== '' && (
          <Message $status={status}>
            <TextLine>{message}</TextLine>
          </Message>
        )}
        {isPending && (
          <CenterWrapperColumn $totalHeight={maxHeight}>
            <StyledLoading />
            <LoadingText>{__('Loading in progress')}</LoadingText>
          </CenterWrapperColumn>
        )}
        {(status === basketStatuses.OK ||
          status === basketStatuses.VALIDATED ||
          status === basketStatuses.WAITING_TRADER_VALIDATION ||
          status === basketStatuses.ERROR) && (
          <Fragment>
            {showKeyboard ? (
              <Keyboard onChange={onChangeStake} close={onCloseStakeInput} stake={stake} />
            ) : (
              <Sum>
                <TextLine>
                  <LabelYellow>{betTitle}</LabelYellow>
                </TextLine>
                <TextLine>
                  <ShortLabel>{outcomeLabel}</ShortLabel>
                  {status === basketStatuses.VALIDATED && validatedValue && value !== validatedValue ? (
                    <RightLabel>
                      <strike>{value}</strike> {validatedValue > value ? <ArrowUp /> : <ArrowDown />} {validatedValue}
                    </RightLabel>
                  ) : (
                    <RightLabel $highlight={valueHasChanged}>{Formatter.formatOdd(value)}</RightLabel>
                  )}
                </TextLine>
                {status !== basketStatuses.VALIDATED && status !== basketStatuses.WAITING_TRADER_VALIDATION ? (
                  <StyledStakeInput
                    stake={stake}
                    onChange={onChangeStake}
                    onClickPlus={onClickPlusStake}
                    onClickMinus={onClickMinusStake}
                    onClick={onClickStakeInput}
                  />
                ) : (
                  <TextLine>
                    <ShortLabel>{__('Wager')}</ShortLabel>
                    <RightLabel>{Formatter.formatNumber(parseFloat(stake, 10), getCurrency())}</RightLabel>
                  </TextLine>
                )}
                <TextLine>
                  <ShortLabel>{__('Potential winnings')}</ShortLabel>
                  <RightLabel>
                    {Formatter.formatNumber(globalBasketUtils.computePotentialGain(value, stake), getCurrency())}
                  </RightLabel>
                </TextLine>
              </Sum>
            )}
          </Fragment>
        )}
      </Wrap>
      {!showKeyboard && (
        <Cta ref={fixedBottomComponentRef}>
          {status !== basketStatuses.WAITING_TRADER_VALIDATION &&
            status !== basketStatuses.PENDING &&
            status !== basketStatuses.VALIDATED && (
              <ClearButton onClick={onClearClick}>
                <TrashIcon />
              </ClearButton>
            )}
          {status === basketStatuses.VALIDATED && (
            <TertiaryButton onClick={onClearClick}>{__('!action:deleteAndContinue')}</TertiaryButton>
          )}
          {!valueHasChanged && (status === basketStatuses.OK || status === basketStatuses.ERROR) && (
            <MainButton disabled={stake <= 0} onClick={onValidateClick}>
              {__('!action:Bet')}
            </MainButton>
          )}
          {valueHasChanged &&
            status !== basketStatuses.PENDING &&
            status !== basketStatuses.WAITING_TRADER_VALIDATION &&
            status !== basketStatuses.VALIDATED && (
              <MainButton onClick={acceptChange}>{__('Accept changes')}</MainButton>
            )}
        </Cta>
      )}
    </Fragment>
  );
};

export default ImmersiveBasket;
